/**
 * ContactNotesButton
 * -----------------------------------------------------------------------------
 * Atalho do FAB do chat para gerenciar anotações do contato atual.
 *   - Badge com contagem de anotações NÃO LIDAS pelo usuário atual.
 *   - Clique SEMPRE abre o modal de listagem (mesmo com 0 itens),
 *     permitindo visualizar anotações já lidas ou criar uma nova
 *     a partir do empty state / botão "Nova".
 *
 * @version 1.1.0
 * RELEASE: 2026-06-03 v1.1.0 - Clique sempre abre listagem; removida abertura direta do editor.
 * RELEASE: 2026-06-03 v1.0.0 - Criação inicial.
 */
import { useEffect, useId, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardEdit } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

import { ConversationContactNotesModal } from './ConversationContactNotesModal';

interface ContactNotesButtonProps {
  contactId?: string;
  /** Quando true, exibe contador (badge) de não lidas. */
  showCount?: boolean;
}

export function ContactNotesButton({
  contactId,
  showCount = false,
}: ContactNotesButtonProps) {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();

  // ID único por instância — evita colisão de canal Realtime quando o mesmo
  // botão é montado em mais de um lugar (ex.: página /tenant/chat + modal
  // ChatConversationModal sobreposto via DealDetailsModalPremium).
  const instanceId = useId();

  const [openList, setOpenList] = useState(false);

  // Contagem de não lidas para o badge.
  const { data: total = 0 } = useQuery({
    queryKey: ['contact-notes-count', contactId, currentTenant?.id],
    enabled: showCount && !!contactId && !!currentTenant?.id,
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_contact_notes_count', {
        p_contact_id: contactId,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
      return Number(data ?? 0);
    },
  });

  // Realtime: invalida o contador.
  useEffect(() => {
    if (!showCount || !contactId) return;
    const channel = supabase
      .channel(`contact-notes-count-${contactId}-${instanceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contact_notes',
          filter: `contact_id=eq.${contactId}`,
        },
        () => {
          qc.invalidateQueries({
            queryKey: ['contact-notes-count', contactId],
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [contactId, showCount, qc, instanceId]);

  const hasUnread = total > 0;
  const badgeLabel = total > 9 ? '9+' : String(total);

  const tooltipText = hasUnread
    ? `${total} anotação(ões) não lida(s)`
    : 'Anotações do contato';

  const handleClick = () => {
    if (!contactId) return;
    // Sempre abre a listagem; o próprio modal trata empty state e criação.
    setOpenList(true);
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              'relative h-11 w-11',
              hasUnread
                ? 'text-primary'
                : 'text-foreground hover:text-primary',
            )}
            disabled={!contactId}
            onClick={handleClick}
            aria-label="Anotações do contato"
          >
            <ClipboardEdit className="h-5 w-5" />
            {showCount && hasUnread && (
              <span
                className={cn(
                  'absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full',
                  'flex items-center justify-center text-[10px] font-semibold leading-none',
                  'bg-primary text-primary-foreground',
                )}
              >
                {badgeLabel}
              </span>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>

      {/* Listagem (sempre, mesmo com 0 não lidas) */}
      {openList && contactId && (
        <ConversationContactNotesModal
          open={openList}
          onOpenChange={setOpenList}
          contactId={contactId}
        />
      )}
    </>
  );
}
